import React from "react";

export default function JobPriceBreakdownApiDoc() {
  return (
    <div className="text-gray-800 text-md leading-7 px-4 min-w-0 max-w-[40rem] lg:mt-0 lg:mr-0 lg:max-w-[50rem]">
      {/* Header */}
      <header className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-50 via-white to-transparent" />
        <div className="relative p-8 sm:p-12">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            Jobs API — Job Price Breakdown
          </h1>
          <p className="mt-3 max-w-3xl text-base sm:text-lg leading-7 text-slate-600">
            Retrieve the pricing breakdown for a single job, including services,
            discounts, fees, subtotals, and total price. This endpoint requires
            authentication and applies{" "}
            <span className="font-semibold">role-based price visibility</span>.
          </p>

          {/* Quick facts */}
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Method
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900">GET</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Auth
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900">JWT</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Endpoint
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900">
                /api/jobs/price-breakdown/{`{id}`}/
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="mt-10 rounded-3xl border border-slate-200 bg-white p-6 sm:p-10 shadow-sm">
        {/* Overview */}
        <section>
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Overview
          </h2>
          <p className="mt-3 leading-7 text-slate-700">
            Returns a structured price breakdown for a job. The response may be
            built in one of two ways depending on the job state:
          </p>

          <ul className="mt-3 grid list-disc gap-2 pl-5 text-slate-700 marker:text-slate-400">
            <li>
              If the job is already{" "}
              <code className="font-mono">Invoiced (status = "I")</code> and
              invoiced services exist, the endpoint returns the{" "}
              <span className="font-semibold">saved invoiced breakdown</span>.
            </li>
            <li>
              Otherwise, the endpoint calculates the breakdown dynamically using
              current pricing rules.
            </li>
            <li>
              For some project-manager scenarios, the calculation may use a{" "}
              <span className="font-semibold">
                vendor-customer mapped price list
              </span>
              .
            </li>
            <li>
              The final <code className="font-mono">totalPrice</code> is
              returned as a formatted string with two decimals.
            </li>
          </ul>
        </section>

        {/* Request */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Request
          </h2>

          <div className="mt-4">
            {/* HTTP */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-sm font-semibold text-slate-900">HTTP</div>
              <ul className="mt-2 space-y-1 text-sm leading-6 text-slate-700">
                <li>
                  <span className="font-semibold">Method:</span>{" "}
                  <code className="font-mono">GET</code>
                </li>
                <li>
                  <span className="font-semibold">URL:</span>{" "}
                  <code className="font-mono">
                    /api/jobs/price-breakdown/{`{id}`}/
                  </code>
                </li>
                <li>
                  <span className="font-semibold">Headers:</span>
                  <br />
                  <code className="font-mono">
                    Authorization: JWT your_token
                  </code>
                </li>
              </ul>
            </div>

            {/* Path params */}
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-sm font-semibold text-slate-900">
                Path parameters
              </div>
              <ul className="mt-2 space-y-2 text-sm leading-6 text-slate-700">
                <li>
                  <code className="font-mono">id</code> —{" "}
                  <span className="font-mono">number</span>
                  <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                    required
                  </span>{" "}
                  Job ID to retrieve pricing for.
                </li>
              </ul>
            </div>
          </div>

          {/* Example: cURL */}
          <div className="mt-6 not-prose my-6 overflow-hidden rounded-2xl bg-zinc-900 shadow-md dark:ring-1 dark:ring-white/10">
            <div className="flex min-h-[calc(theme(spacing.12)+1px)] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4 dark:border-zinc-800 dark:bg-transparent">
              <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                Request
              </h3>
            </div>
            <div className="flex h-9 items-center gap-2 px-4 border-b border-zinc-700">
              <div className="dark flex">
                <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-500 dark:text-emerald-400">
                  GET
                </span>
              </div>
              <span className="h-0.5 w-0.5 rounded-full bg-zinc-500"></span>
              <span className="font-mono text-xs text-zinc-400">
                /api/jobs/price-breakdown/{`{id}`}/
              </span>
            </div>
            <div className="group dark:bg-white/2.5 pb-3">
              <div className="relative">
                <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                  {`curl -X GET https://api.livetakeoff.com/api/jobs/price-breakdown/9876/ \\
  -H "Authorization: JWT YOUR_ACCESS_TOKEN"`}
                </pre>
              </div>
            </div>
          </div>
        </section>

        {/* Response */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Response
          </h2>

          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900">
              200 OK (shape overview)
            </div>
            <p className="mt-2 text-sm leading-6 text-slate-700">
              The response contains the main price list label, aircraft type,
              services, discounts, fees, subtotal values, and total.
            </p>

            <ul className="mt-3 grid list-disc gap-2 pl-5 text-slate-700 marker:text-slate-400">
              <li>
                <code className="font-mono">priceListType</code> — name of the
                price list in uppercase.
              </li>
              <li>
                <code className="font-mono">aircraftType</code> — aircraft type
                name.
              </li>
              <li>
                <code className="font-mono">services</code> — array of services
                with name and price.
              </li>
              <li>
                <code className="font-mono">servicesPrice</code> — sum of all
                service prices before discounts/fees.
              </li>
              <li>
                <code className="font-mono">discounts</code> — array of applied
                discounts.
              </li>
              <li>
                <code className="font-mono">discountedPrice</code> — subtotal
                after discounts.
              </li>
              <li>
                <code className="font-mono">additionalFees</code> — array of
                applied fees.
              </li>
              <li>
                <code className="font-mono">totalPrice</code> — final formatted
                total.
              </li>
            </ul>

            {/* Example response */}
            <div className="mt-4 not-prose my-6 overflow-hidden rounded-2xl bg-zinc-900 shadow-md dark:ring-1 dark:ring-white/10">
              <div className="flex min-h-[calc(theme(spacing.12)+1px)] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4 dark:border-zinc-800 dark:bg-transparent">
                <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                  Response
                </h3>
              </div>
              <div className="flex h-9 items-center gap-2 px-4 border-b border-zinc-700">
                <div className="dark flex">
                  <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-500 dark:text-emerald-400">
                    JSON
                  </span>
                </div>
                <span className="h-0.5 w-0.5 rounded-full bg-zinc-500"></span>
                <span className="font-mono text-xs text-zinc-400">
                  application/json
                </span>
              </div>
              <div className="group dark:bg-white/2.5 pb-3">
                <div className="relative">
                  <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                    {`{
  "priceListType": "STANDARD",
  "aircraftType": "Gulfstream G550",
  "services": [
    {
      "name": "Basic Exterior",
      "price": 450
    },
    {
      "name": "Interior Level 1",
      "price": 275
    }
  ],
  "servicesPrice": 725,
  "discounts": [
    {
      "name": "G",
      "discount": 10,
      "isPercentage": true,
      "discount_dollar_amount": 72.5
    }
  ],
  "discountedPrice": 652.5,
  "additionalFees": [
    {
      "name": "A",
      "fee": 100,
      "isPercentage": false,
      "additional_fee_dollar_amount": 100
    }
  ],
  "totalPrice": "752.50"
}`}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Invoiced behavior */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Invoiced jobs behavior
          </h2>

          <p className="mt-3 leading-7 text-slate-700">
            If the job status is <code className="font-mono">"I"</code> and
            invoiced services exist, this endpoint does not calculate live
            pricing. Instead, it returns the already-saved invoiced values from:
          </p>

          <ul className="mt-3 grid list-disc gap-2 pl-5 text-slate-700 marker:text-slate-400">
            <li>
              <code className="font-mono">InvoicedService</code>
            </li>
            <li>
              <code className="font-mono">InvoicedDiscount</code>
            </li>
            <li>
              <code className="font-mono">InvoicedFee</code>
            </li>
          </ul>

          <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5">
            <div className="text-sm font-semibold text-amber-900">
              Important note
            </div>
            <p className="mt-2 text-sm leading-6 text-amber-900">
              This means an invoiced job’s price breakdown reflects the{" "}
              <span className="font-semibold">invoice snapshot</span>, not
              necessarily the current live pricing rules.
            </p>
          </div>
        </section>

        {/* Dynamic pricing behavior */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Dynamic pricing behavior
          </h2>

          <p className="mt-3 leading-7 text-slate-700">
            If the job is not already invoiced with saved invoiced services, the
            breakdown is generated through{" "}
            <code className="font-mono">PriceBreakdownService</code>.
          </p>
        </section>

        {/* Permissions & rules */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Permissions & rules
          </h2>

          <p className="mt-3 leading-7 text-slate-700">
            The server checks whether the user is allowed to view pricing for
            the job before returning any breakdown.
          </p>

          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900">
              Who can see the price breakdown?
            </div>
            <ul className="mt-2 grid list-disc gap-2 pl-5 text-slate-700 marker:text-slate-400">
              <li>Superusers and staff users.</li>
              <li>
                Users with <span className="font-semibold">show_job_price</span>{" "}
                enabled on their profile.
              </li>
              <li>
                Users in the{" "}
                <span className="font-semibold">Account Managers</span> group.
              </li>
              <li>
                Customer users, but only if the customer’s settings allow job
                price visibility.
              </li>
              <li>
                Customer users must belong to the job’s customer, including
                extra customer mappings via{" "}
                <code className="font-mono">UserCustomer</code>.
              </li>
              <li>
                Portside Integration users may only view the breakdown if the
                job was created by Portside.
              </li>
            </ul>
          </div>

          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900">
              Customer visibility requirement
            </div>
            <p className="mt-2 text-sm leading-6 text-slate-700">
              For customer users, access is only granted when{" "}
              <code className="font-mono">
                job.customer.customer_settings.show_job_price
              </code>{" "}
              is enabled.
            </p>
          </div>
        </section>

        {/* Errors */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Errors
          </h2>

          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900">Errors</div>
            <ul className="mt-2 space-y-1 text-sm leading-6 text-slate-700">
              <li>
                <span className="font-mono">403</span> — you do not have
                permission to see the price breakdown.
              </li>
              <li>
                <span className="font-mono">404</span> — job not found.
              </li>
            </ul>

            <div className="mt-4 not-prose my-6 overflow-hidden rounded-2xl bg-zinc-900 shadow-md dark:ring-1 dark:ring-white/10">
              <div className="flex min-h-[calc(theme(spacing.12)+1px)] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4 dark:border-zinc-800 dark:bg-transparent">
                <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                  Response
                </h3>
              </div>
              <div className="flex h-9 items-center gap-2 px-4 border-b border-zinc-700">
                <div className="dark flex">
                  <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-500 dark:text-emerald-400">
                    JSON
                  </span>
                </div>
                <span className="h-0.5 w-0.5 rounded-full bg-zinc-500"></span>
                <span className="font-mono text-xs text-zinc-400">
                  application/json
                </span>
              </div>
              <div className="group dark:bg-white/2.5 pb-3">
                <div className="relative">
                  <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                    {`{
  "error": "You do not have permission to see the price breakdown"
}`}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
